import { inject, Injectable, signal } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { UserPreference } from '../../shared/models/user-preference.model';
import { LocalStorageService } from './local-storage.service';
import { LocalStorageKeys } from '../constants/local-storage.constants';

@Injectable({
  providedIn: 'root',
})
export class AppStateService {
  isSidebarOpen = signal(false);
  userPreferences = signal<UserPreference>({ darkMode: false });
  private headerTitle = new BehaviorSubject<string>('VOTA');
  title$ = this.headerTitle.asObservable();
  localStorageService = inject(LocalStorageService);

  constructor() {}

  toggleSidebar(): void {
    this.isSidebarOpen.update((open) => !open);
  }

  setTitle(newTitle: string) {
    this.headerTitle.next(newTitle);
  }

  toggleThememode(): void {
    let newMode: boolean;

    this.userPreferences.update((current) => {
      newMode = !current.darkMode;
      this.onModeSelection(newMode);
      return {
        ...current,
        darkMode: !current.darkMode,
      };
    });
  }

  setThemeMode(isDarkMode: boolean): void {
    this.userPreferences.update((current) => ({
      ...current,
      darkMode: isDarkMode,
    }));
    this.onModeSelection(isDarkMode);
  }

  private onModeSelection(isDarkMode: boolean): void {
    if (isDarkMode) {
      this.enableDarkMode();
    } else {
      this.disableDarkMode();
    }
    this.updateTheme(isDarkMode);
  }

  private enableDarkMode(): void {
    document.documentElement.classList.add('dark');
  }

  private disableDarkMode(): void {
    document.documentElement.classList.remove('dark');
  }

  private updateTheme(darkMode: boolean): void {
    const userConfigurationData =
      this.localStorageService.getItemWithExpiry<UserPreference | null>(
        LocalStorageKeys.USER_PREFERENCES
      );

    if (!userConfigurationData) {
      const userPreference: UserPreference = {
        darkMode: darkMode,
      };
      this.localStorageService.setItemWithExpiry(
        LocalStorageKeys.USER_PREFERENCES,
        userPreference
      );
      return;
    }

    const userConfiguration: UserPreference = {
      ...userConfigurationData,
      darkMode: darkMode,
    };
    this.localStorageService.setItemWithExpiry(
      LocalStorageKeys.USER_PREFERENCES,
      userConfiguration
    );
  }
}
